// Migrated
<template>
  <div
    ref="container"
    class="relative size-full"
  >
    <b-skeleton
      v-if="!mapboxStaticMapUrl || loading"
      width="100%"
      height="100%"
      class="object-cover size-full absolute top-0"
    />
    <img
      v-else
      class="object-cover size-full"
      :src="mapboxStaticMapUrl"
      :alt="alt"
      :loading="lazy ? 'lazy' : 'eager'"
    />
  </div>
</template>

<script>
import throttle from 'lodash.throttle'

export default defineNuxtComponent({
  setup () {
    const { public: { mapbox: { apiKey: mapboxApiKey, style: mapboxStyle } } } = useRuntimeConfig()

    return {
      mapboxApiKey,
      mapboxStyle,
    }
  },

  props: {
    /**
     * @prop {[lng, lat]} center
     */
    center: {
      type: Array,
      required: true,
    },

    zoom: {
      type: Number,
      default: 9,
    },

    /**
     * @prop {[[lng, lat]]} markers
     */
    markers: {
      type: Array,
      default: () => [],
    },

    lazy: {
      type: Boolean,
      default: false,
    },

    alt: {
      type: String,
      default: 'Map',
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      mapboxStaticMapUrl: null,
      markerIcon: formatCloudinaryImagePath('mapmarker', {
        transform: false,
        isSol: this.$isSol,
      }),
      maxLongitude: 180,
    }
  },

  computed: {
    gmapToMapboxZoom () {
      return this.zoom - 1
    },
  },

  watch: {
    center () {
      this.setMapboxStaticMapUrl()
    },

    zoom () {
      this.setMapboxStaticMapUrl()
    },

    markers () {
      this.setMapboxStaticMapUrl()
    },
  },

  mounted () {
    this.$nextTick(this.setMapboxStaticMapUrl)
    if (this.$refs.container) {
      this.resizeThrottle = throttle(this.setMapboxStaticMapUrl, 500)
      this.resizeObserver = new ResizeObserver(this.resizeThrottle)
      this.resizeObserver.observe(this.$refs.container)
    }
  },

  beforeUnmount () {
    this.resizeObserver?.disconnect()
  },

  methods: {
    setMapboxStaticMapUrl () {
      if (!process.browser || !this.$refs.container) {
        return
      }
      let { offsetHeight: height, offsetWidth: width } = this.$refs.container

      if (height <= 1 || width <= 1) {
        return
      }

      const maxSize = 1280

      if (height > maxSize) {
        height = maxSize
      }

      if (width > maxSize) {
        width = maxSize
      }

      // eslint-disable-next-line prefer-const
      let [lng, lat] = this.center
      if (lng < 1 - this.maxLongitude) {
        lng += 360
      }
      if (lng > this.maxLongitude) {
        lng -= 360
      }

      const markers = this.markers.map(([lng, lat]) => `url-${encodeURIComponent(this.markerIcon)}(${lng?.toFixed(6)},${lat?.toFixed(6)})`).join(',')

      const url = `https://api.mapbox.com/styles/v1/${this.mapboxStyle}/static/${markers}/${lng},${lat},${this.gmapToMapboxZoom}/${width}x${height}?access_token=${this.mapboxApiKey}`
      preloadImage(url)
        .then(() => (this.mapboxStaticMapUrl = url))
    },
  },
})
</script>
